import { Controller, type Control, type UseFormRegister, type FieldErrors } from 'react-hook-form';
import type { PurchaseOrderFormData } from '../../../../types/purchaseOrder';
import type { Supplier } from '../../../../types/supplier';
import { AutocompleteSelect } from '../../../../components/common/AutocompleteSelect';
import { useLanguage } from '../../../../features/language/useLanguage';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../../utils/date';

interface POGeneralInfoSectionProps {
  control: Control<PurchaseOrderFormData>;
  register: UseFormRegister<PurchaseOrderFormData>;
  errors: FieldErrors<PurchaseOrderFormData>;
  readOnly: boolean;
  suppliers: Supplier[];
  supplierInternalNotes: string;
  onSupplierInternalNotesChange: (value: string) => void;
  onSupplierSelect: (supplierId: string | null) => void;
  statusDisabled: boolean;
}

/** Supplier, supplier note, order date and order status fields (create mode only). */
export function POGeneralInfoSection({
  control,
  register,
  errors,
  readOnly,
  suppliers,
  supplierInternalNotes,
  onSupplierInternalNotesChange,
  onSupplierSelect,
  statusDisabled,
}: POGeneralInfoSectionProps) {
  const { t } = useLanguage();
  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
        {t('purchaseOrder.form.generalInformation')}
      </h3>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Controller
            control={control}
            name="supplier"
            render={({ field }) => (
              <AutocompleteSelect
                dataTestId="po-create-supplier-input"
                label={t('purchaseOrder.form.fields.supplier')}
                value={field.value}
                disabled={readOnly}
                onChange={(value, option) => {
                  onSupplierSelect(option?.id ?? null);
                  field.onChange(option?.text ?? value);
                }}
                // The dropdown filters on `label`, so showing the code there makes
                // it searchable: typing a code hits the real supplier instead of
                // only offering to create one named after the code.
                options={suppliers
                  .filter((s) => s.status !== 'Inactive')
                  .map((s) => ({ id: s.id, label: s.code ? `${s.name} (${s.code})` : s.name, text: s.name }))}
                error={errors.supplier?.message}
                placeholder={t('purchaseOrder.form.fields.supplierPlaceholder')}
              />
            )}
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.form.fields.supplierInternalNote')}</label>
          <textarea
            value={supplierInternalNotes}
            onChange={(e) => onSupplierInternalNotesChange(e.target.value)}
            disabled={readOnly}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            placeholder={t('purchaseOrder.form.fields.supplierInternalNotePlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.form.fields.orderDate')}</label>
          <input
            data-test-id="po-create-order-date"
            type="date"
            {...register('orderDate')}
            onClick={openNativeDatePickerSafely}
            onKeyDown={preventManualDateEntry}
            onPaste={preventManualDatePasteOrDrop}
            onDrop={preventManualDatePasteOrDrop}
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500"
          />
          {errors.orderDate && <p className="mt-1 text-sm text-red-600">{errors.orderDate.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.form.fields.orderStatus')}</label>
          <select
            data-test-id="po-create-status"
            {...register('status')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500"
            disabled={statusDisabled}
          >
            <option value="draft">{t('purchaseOrder.filters.options.draft')}</option>
            <option value="ordered">{t('purchaseOrder.filters.options.ordered')}</option>
            <option value="cancelled">{t('purchaseOrder.filters.options.cancelled')}</option>
          </select>
        </div>
      </div>
    </div>
  );
}
